/* ============================================================
 * frontend-v3/src/components/tags/TagAutocompleteInput.tsx
 *
 * NMW-410/413 — reusable tag entry control: a kind <Select> + a text
 * <Input> backed by autocomplete suggestions from the canonical
 * `tagRegistry` catalog (`api.assetQueries.listTagRegistry`).
 *
 * Behavior:
 *   - Suggestions are fetched (debounced) ONLY while the input is focused,
 *     so the control holds no live subscription at rest.
 *   - ↑/↓ move the active suggestion, Enter picks it, Esc closes.
 *   - Enter on text with no active suggestion emits a create-new entry
 *     `{ tag, kind }` using the current kind Select value.
 *   - Picking a suggestion emits the suggestion's OWN kind (a word can be
 *     both a channel and a category — the catalog row's kind wins).
 *
 * Honest-state (CLAUDE.md §5): `undefined` suggestions → nothing shown
 * (a dropdown needs no skeleton); a resolved-but-empty result while the
 * operator is typing → "sin coincidencias".
 *
 * The component performs NO mutation and fabricates NO result — it only
 * reads the registry and lifts the chosen entry to its parent via onPick.
 * ============================================================ */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { Input, Select } from '../primitives';

export type TagKind = 'channel' | 'program' | 'category' | 'freeform';
export interface TagEntry {
  tag: string;
  kind: TagKind;
}

export const TAG_KIND_OPTIONS: ReadonlyArray<{ value: TagKind; label: string }> = [
  { value: 'freeform', label: 'Etiqueta' },
  { value: 'category', label: 'Categoría' },
  { value: 'channel', label: 'Canal' },
  { value: 'program', label: 'Programa' },
];
export const TAG_KIND_LABEL: Record<TagKind, string> = {
  freeform: 'Etiqueta',
  category: 'Categoría',
  channel: 'Canal',
  program: 'Programa',
};

interface Suggestion {
  tag: string;
  normalizedTag: string;
  kind: TagKind;
  usageCount: number;
}

/** Normalized identity — mirrors the backend `normalizeTag` (trim + lowercase). */
function norm(s: string): string {
  return s.trim().toLowerCase();
}

export function TagAutocompleteInput({
  kind,
  onKindChange,
  onPick,
  placeholder = 'Añadir etiqueta…',
  scopeKind = false,
}: {
  kind: TagKind;
  onKindChange: (k: TagKind) => void;
  onPick: (entry: TagEntry) => void;
  placeholder?: string;
  /** When true, the registry query is scoped to the current kind facet. */
  scopeKind?: boolean;
}) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [debounced, setDebounced] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 200);
    return () => clearTimeout(t);
  }, [text]);

  // Only subscribe while focused — no live query at rest, and it keeps the
  // dropdown from flashing when the control is not in use.
  //
  // BLOCKER-2: read `listTagVocabulary` (registry catalog UNIONed with the
  // live in-use tags), not `listTagRegistry` alone — otherwise autocomplete
  // suggests nothing on any deployment whose tags predate the registry
  // backfill. minUsage is omitted (0) so the full catalog stays reusable.
  const data = useQuery(
    api.assetQueries.listTagVocabulary,
    focused
      ? { search: debounced || undefined, limit: 8, ...(scopeKind ? { kind } : {}) }
      : 'skip',
  ) as { tags: Suggestion[]; count: number } | undefined;

  const suggestions = useMemo(() => data?.tags ?? [], [data]);
  const open = focused && text.trim().length > 0;

  function commit(entry: TagEntry) {
    const tag = entry.tag.trim();
    if (!tag) return;
    onPick({ tag, kind: entry.kind });
    setText('');
    setDebounced('');
    setActiveIdx(-1);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIdx >= 0 && activeIdx < suggestions.length) {
        const s = suggestions[activeIdx];
        commit({ tag: s.tag, kind: s.kind });
      } else {
        // MINOR-5: an un-arrowed Enter normally creates a new entry. But if the
        // typed value EXACTLY matches an existing suggestion of the SAME kind,
        // adopt that catalog entry (its canonical display casing) instead of
        // minting a near-duplicate that splinters the vocabulary.
        const typed = norm(text);
        const exact =
          typed.length > 0
            ? suggestions.find((s) => s.normalizedTag === typed && s.kind === kind)
            : undefined;
        if (exact) {
          commit({ tag: exact.tag, kind: exact.kind });
        } else {
          // Create-new: use the Select's current kind.
          commit({ tag: text, kind });
        }
      }
    } else if (e.key === 'Escape') {
      setFocused(false);
      setActiveIdx(-1);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', position: 'relative' }}>
      <div style={{ width: 150, flexShrink: 0 }}>
        <Select
          value={kind}
          onChange={(v: string) => onKindChange(v as TagKind)}
          full
          options={TAG_KIND_OPTIONS as Array<{ value: TagKind; label: string }>}
        />
      </div>
      <div
        style={{ flex: 1, position: 'relative' }}
        // focusin/focusout bubble, so capturing on this wrapper reliably
        // tracks the descendant <input>'s focus. A short blur delay lets a
        // suggestion click land before the dropdown unmounts.
        onFocusCapture={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setFocused(true);
        }}
        onBlurCapture={() => {
          blurTimer.current = setTimeout(() => setFocused(false), 120);
        }}
      >
        <Input
          value={text}
          onChange={(v: string) => {
            setText(v);
            setActiveIdx(-1);
          }}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
        />
        {open && (
          <div
            role="listbox"
            onMouseDown={() => {
              // Keep focus through the click so onBlur doesn't close first.
              if (blurTimer.current) clearTimeout(blurTimer.current);
            }}
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 30,
              background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto',
            }}
          >
            {data === undefined ? null : suggestions.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted-slate)' }}>
                Sin coincidencias — Enter crea «{text.trim()}»
              </div>
            ) : (
              suggestions.map((s, i) => (
                <button
                  key={`${s.kind}:${s.normalizedTag}`}
                  type="button"
                  role="option"
                  aria-selected={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit({ tag: s.tag, kind: s.kind })}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8, padding: '8px 12px', border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: i === activeIdx ? 'var(--lightest-gray)' : 'transparent',
                    fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-muted)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {s.kind !== 'freeform' && (
                      <span style={{ fontSize: 10, opacity: 0.65 }}>{TAG_KIND_LABEL[s.kind]}:</span>
                    )}
                    {s.tag}
                  </span>
                  <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{s.usageCount}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TagAutocompleteInput;
